import { useEffect, useRef, useState } from "react";
import { Button, ErrorNote } from "../../components/ui";
import { formatMoney } from "../../lib/money";
import {
  loadStripe, createChargeIntent, recordChargePayment,
  type PaidCharge, type StripeLike,
} from "../../lib/stripe";

/**
 * The card field for a charge, used by both sides.
 *
 * At the desk it is handed a chargeId; on a payment link it is handed a
 * token. Neither one carries an amount, because the amount is read out of the
 * charges row by the worker — the point of the split is that nothing in a
 * browser gets to say what is charged. The figure shown here is for the person
 * looking at it and has no part in the charge.
 *
 * The number itself is typed into an iframe served by Stripe. It does not
 * enter this component, this bundle or the database, which is what keeps
 * FrontLot on PCI SAQ A.
 *
 * Charging is two steps on purpose. Stripe confirms the card; then the worker
 * asks Stripe whether it really succeeded before anything is written down. A
 * browser cannot talk its way into a payment that did not happen.
 */
export default function ChargeCard({
  ref_, amountCents, onPaid, onCancel, payLabel = "Charge",
}: {
  /** How the worker is to find the charge. One or the other, never both. */
  ref_: { chargeId: string } | { token: string };
  amountCents: number;
  /** Handed the row the worker wrote, so the caller can show a receipt
      without reading the charge back — a paid payment link is retired and
      cannot be read again. */
  onPaid: (paid: PaidCharge | null) => void;
  onCancel?: () => void;
  payLabel?: string;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  /* "stranded" is terminal on purpose. The card has been charged and the row
     has not been written, and the one thing that must not happen next is a
     second charge — so the button does not come back. The deal screen had
     this wrong: it dropped back to idle with the card field still complete,
     and a clerk reading "do not take it again" could take it again with one
     click. */
  const [stage, setStage] =
    useState<"idle" | "charging" | "recording" | "stranded">("idle");

  const stripeRef = useRef<StripeLike | null>(null);
  const cardRef = useRef<{ destroy(): void } | null>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    loadStripe()
      .then((stripe) => {
        if (!live || !mountRef.current) return;
        stripeRef.current = stripe;
        const card = stripe.elements().create("card", {
          hidePostalCode: false,
          style: {
            base: {
              fontSize: "16px",          // under 16px, iOS zooms on focus
              color: "#141C24",
              fontFamily: "Inter, system-ui, sans-serif",
              "::placeholder": { color: "#68727D" },
            },
            invalid: { color: "#B3352B" },
          },
        });
        card.mount(mountRef.current);
        card.on("change", (e) => {
          setCardError(e.error?.message ?? null);
          setComplete(Boolean(e.complete));
        });
        cardRef.current = card;
        setReady(true);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not load Stripe."));
    return () => {
      live = false;
      cardRef.current?.destroy();
      cardRef.current = null;
    };
  }, []);

  async function charge() {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return;

    setErr(null);
    setStage("charging");
    /* Tracked separately from `stage`: the catch below needs to know whether
       the money had already moved, and reading state inside a catch gets the
       value from the render that started the call, not the current one. */
    let moved = false;
    try {
      const { clientSecret } = await createChargeIntent(ref_);
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: card as unknown as object },
      });
      if (result.error) {
        // A declined card is the customer's bank talking, not a fault here.
        setErr(result.error.message);
        setStage("idle");
        return;
      }
      const intent = result.paymentIntent;
      if (!intent || intent.status !== "succeeded") {
        setErr(`The payment did not complete (${intent?.status ?? "unknown"}).`);
        setStage("idle");
        return;
      }

      // The money has moved. From here the row must be written, so a failure
      // says so loudly rather than losing a real payment quietly.
      moved = true;
      setStage("recording");
      const written = await recordChargePayment(intent.id);
      onPaid(written.charge ?? null);
    } catch (e) {
      setErr(
        (e instanceof Error ? e.message : "Something went wrong.")
        + (moved
            ? " The card WAS charged — do not take it again. Note the amount, "
              + "check Stripe, and call the dealership before trying anything else."
            : ""));
      setStage(moved ? "stranded" : "idle");
    }
  }

  const canCharge = ready && complete && stage === "idle" && amountCents > 0;

  return (
    <>
      <div className="field">
        <label>Card</label>
        <div className="cardfield" ref={mountRef} />
        {!ready && !err && <p className="hint dim">Loading the secure card field</p>}
        {cardError && <p className="hint err">{cardError}</p>}
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}

      <div className="actions">
        <Button onClick={charge} disabled={!canCharge}>
          {stage === "charging" ? "Charging"
            : stage === "recording" ? "Recording"
            : stage === "stranded" ? "Charged — do not retry"
            : `${payLabel} ${formatMoney(amountCents)}`}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}
                  disabled={stage === "charging" || stage === "recording"}>
            {stage === "stranded" ? "Close" : "Cancel"}
          </Button>
        )}
      </div>

      <p className="hint dim">
        The card is typed into Stripe's own secure field. The number does not
        pass through FrontLot and is never stored — only the brand and last four
        digits are kept with the payment.
      </p>
    </>
  );
}
